// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { mergeWithCss, xIsAuthenticated } from "@adl/XDL/Utilities";
import logger from "@insite/client-framework/Logger";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentUserIsGuest, getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import React, { FC, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    labelOverride?: string;
    extendedStyles?: ButtonPresentationProps;
}

const mapStateToProps = (state: ApplicationState) => ({
    isSignInPage: getCurrentPage(state).type === "SignInPage",
    userName: state.context.session.userName,
    signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
    currentUserIsGuest: getCurrentUserIsGuest(state),
    currentLocation: getLocation(state),
    isAuthenticated: xIsAuthenticated(state),
});

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export const signInButtonStyles: ButtonPresentationProps = {
    variant: "secondary",
    mergeCss: true,
    typographyProps: {
        size: 10,
        css: css`
            line-height: 14px;
        `,
    },
};

const SignInButton: FC<Props> = ({
    labelOverride,
    isAuthenticated,
    signInUrl,
    currentUserIsGuest,
    userName,
    currentLocation,
    isSignInPage,
    extendedStyles,
}) => {
    const [styles] = useState(() => mergeWithCss(signInButtonStyles, extendedStyles));
    const history = useHistory();
    if (isAuthenticated) {
        return null;
    }

    const onSignInHandler = () => {
        if ((!userName || currentUserIsGuest) && !isSignInPage) {
            if (!signInUrl) {
                logger.warn("No url was found for SignInPage, defaulting to /SignIn");
                history.push("/MyAccount/SignIn");
            } else {
                const returnUrl = currentLocation.pathname + currentLocation.search;
                if (returnUrl === "/") {
                    history.push(`${signInUrl}`);
                } else {
                    history.push(`${signInUrl}?returnUrl=${encodeURIComponent(returnUrl)}`);
                }
            }
        }
    };

    return (
        <Button onClick={onSignInHandler} {...styles}>
            {labelOverride || translate("Sign In to Build Cart")}
        </Button>
    );
};

export default connect(mapStateToProps)(SignInButton);
